import type { SimpleFieldConfig } from "../../../types/index";

/**
 * Classify a filter field by the KIND of entity its value references.
 *
 * Filter values for these fields are raw scalar UUIDs (e.g. `created_by` stores
 * an `app_users.id`). Rendering that UUID in a chip violates the "never surface
 * a raw UUID" rule and is useless to the user. These predicates let the chip
 * layer resolve the id to a human name via the same lookup caches the table
 * cells use (SmartUserDisplay / SmartCompanyDisplay / SmartContactDisplay).
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when a string is a bare UUID (the shape we must never show unresolved). */
export function looksLikeUuid(value: unknown): boolean {
  return typeof value === "string" && UUID_RE.test(value.trim());
}

/** Field whose value is an `app_users.id` (or array of them). */
export function isUserFilterField(fieldConfig: SimpleFieldConfig): boolean {
  if (fieldConfig.filterType === "user-select") return true;
  if (fieldConfig.type === "user" || fieldConfig.type === "multi-user") return true;
  if (fieldConfig.isForeignKey && fieldConfig.foreignKey?.referencedTable === "app_users") {
    return true;
  }
  return false;
}

/** Field whose value is a `companies.id`. */
export function isCompanyFilterField(fieldConfig: SimpleFieldConfig): boolean {
  if (fieldConfig.filterType === "company-select" || fieldConfig.type === "company-select") {
    return true;
  }
  if (fieldConfig.isForeignKey && fieldConfig.foreignKey?.referencedTable === "companies") {
    return true;
  }
  return false;
}

/** Field whose value is a `contacts.id` (the "client" picker). */
export function isContactFilterField(fieldConfig: SimpleFieldConfig): boolean {
  if (fieldConfig.filterType === "client-select" || fieldConfig.type === "client-select") {
    return true;
  }
  if (fieldConfig.isForeignKey && fieldConfig.foreignKey?.referencedTable === "contacts") {
    return true;
  }
  return false;
}
